/**
 * Shared types for the home timeline cluster (src/app/views/home/timeline/).
 *
 * Owns the enums that previously lived on HomeTimeline / TimelineItemPath
 * so item-level modules (TimelineItem, itemMotion/*) can import a type
 * without importing the HomeTimeline class. (The fields cluster is typed
 * against HomeWebGLDeps from webgl/webgl.types instead.)
 */
import type { HomeStageSlots, HomeWebGLSizes, HomeWebGLType } from 'app/views/home/webgl/webgl.types';
import type { TimelineItem } from 'app/views/home/timeline/TimelineItem';

/** High-level timeline interaction state (which region/scene the user is in). */
export enum TimelineStateType {
	INTRO,
	HOME_LOGO_OVER,
	HOME_LOGO_OUT,
	SCREENS_TRANSITION,
	SCREENS,
	SPACES_TRANSITION,
	SPACES,
	HOME
}

/** Path shape a TimelineItemPath animates its sprite along. */
export enum PathShapeType {
	CIRCLE = 'circle',
	SINE_WAVE = 'sine_wave'
}

/**
 * Frame values TimelineZones samples from HomeTimeline at the top of
 * each update() — the timeline owns resize/drag state, zones only read it.
 * Implemented inline by HomeTimeline when it constructs TimelineZones.
 * `sizes` is the timeline's stable-identity HomeWebGLSizes object (updated in
 * place on resize) — it replaced the old per-metric getters; the genuinely
 * per-frame values (progress/containerX) stay getters.
 */
export interface TimelineZonesDeps {
	getProgress(): number;
	getContainerX(): number;
	sizes: HomeWebGLSizes;
}

/**
 * Frame values / shared refs TimelineCSSSync samples from HomeTimeline —
 * the timeline owns drag/resize state, the CSS sync only reads it.
 * getScreensTvSprites returns the shared TV-frame ITEM sprites (the same
 * sprites HomeScreens.init caches from the item registry); it is null until
 * HomeTimeline._initScreensElements has run, which reproduces HomeScreens'
 * pre-init metric guards exactly.
 */
export interface TimelineCSSSyncDeps {
	getProgress(): number;
	getVelocity(): number;
	getContainerX(): number;
	//The timeline's stable-identity HomeWebGLSizes object (updated in place on
	//resize) — replaced the old getInnerWidth/getVirtualWidth getters:
	sizes: HomeWebGLSizes;
	getScreensTvSprites(): { topLeft: PIXI.Sprite; topRight: PIXI.Sprite } | null;
}

/**
 * Shared refs TimelineDOMTransitions samples from HomeTimeline — getItems
 * returns the shared item registry (storage stays timeline-side; the class
 * only loops it during the work-transition resets). Implemented inline by
 * HomeTimeline when it constructs the class.
 */
export interface TimelineDOMTransitionsDeps {
	getItems(): Array<TimelineItem>;
}

/**
 * Shared refs TimelineSceneColours samples from HomeTimeline — getItems is
 * the id-based item-registry lookup (HomeTimeline._getItemsById; storage and
 * lookup stay timeline-side, the colours class only gathers/tints).
 * Implemented inline by HomeTimeline when it constructs the class.
 */
export interface TimelineSceneColoursDeps {
	getItems(id: string): Array<TimelineItem>;
}

/**
 * Shared refs TimelineItemFactory samples from HomeTimeline — getSlots
 * returns the ordered HomeStageSkeleton layer slots (null until
 * createScene has built them; the factory is only invoked after).
 * getWebGLType is the mounted HomeWebGL implementation's type (the timeline
 * constructs the WebGL layer before the factory, so it is always readable) —
 * createItems filters items by their optional webglTypes list against it.
 * Implemented inline by HomeTimeline when it constructs the factory.
 */
export interface TimelineItemFactoryDeps {
	getSlots(): HomeStageSlots;
	getWebGLType(): HomeWebGLType;
}

/**
 * Callbacks HomeTimeline needs from the controller layer. Implemented by
 * HomeController and threaded through HomeScene.createTimelines — replaces
 * the old inherited this.get('Home') / this.get('ui') kernel lookups.
 */
export interface HomeTimelineDeps {
	/** Sync the paging dots / breakpoint index when a timeline region is entered. */
	setPagingIndex(index: number): void;
	/** Hide the drag-prompt UI (fired entering the Spaces region). */
	hideDragPrompt(): void;
}
